// DegradedExplain bottom sheet（spec 19 §6.1）。tile badge 點擊開啟。
//
// 內容三段（§6.1）：
//   1. 哪個維度超額 / 接近上限 + used/limit 數字 —— 即時重拉 GET /v3/me/quota?device_id=
//      （不吃 list 快照，確保 sheet 內數字為當下回讀，19 §6.3 不本地推算）。
//   2. 後端做了什麼（人話）：模式 B 強制 record_mode=none 或降畫質（02 §7.4.2）。
//   3. 出路 CTA：升級/續訂 → SubscriptionDetail（§3）。
//      ⚠️ v1 無「釋放空間」CTA：recordings 全唯讀（00 §8 GET only），後端無 client 刪 clip
//      端點，storage 僅靠 DDB TTL + S3 Lifecycle 自然到期（缺口 G-6 / GAP-PB-6）—— storage
//      維度文案改述「依保留期自動清除 + 升級才能立即恢復」。
//
// retention_days 取自訂閱（GET /v3/devices/{id}/subscription）作次要、非阻斷資料源；
// 拉不到就省略天數（不阻斷 sheet）。

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { QUOTA_DIMENSION_LABEL_KEY, QUOTA_DIMENSION_UNIT_KEY, quotaPercent, type QuotaDim, type QuotaEntry } from './quotaModel';

interface SubscriptionLite {
  retention_days?: number;
}

/** 維度超額（over）人話說明（§6.1 模式 B）。 */
function explainOver(dim: string, retentionDays: number | null, t: ReturnType<typeof useTranslation>['t']): ReactNode {
  switch (dim) {
    case 'storage_gb':
      return t('account.degradedExplainStorage', { retention: retentionDays != null ? t('account.retentionParen', { days: retentionDays }) : '' });
    case 'relay_gb':
      return t('account.degradeRelay');
    case 'share_count':
      return t('account.degradeShare');
    default:
      return t('account.degradedExplainOverDefault');
  }
}

/** 維度接近上限（warning，軟 80%）人話說明（§6.1）。 */
function explainWarning(dim: string, t: ReturnType<typeof useTranslation>['t']): ReactNode {
  switch (dim) {
    case 'storage_gb':
      return t('account.quotaWarningStorage');
    case 'relay_gb':
      return t('account.quotaWarningRelay');
    case 'share_count':
      return t('account.quotaWarningShare');
    default:
      return t('account.quotaWarningDefault');
  }
}

function dimNumbers(d: QuotaDim, unit: string): string {
  const used = d.used == null ? '—' : `${d.used}${unit}`;
  const hasLimit = d.limit != null && Number.isFinite(d.limit);
  return hasLimit ? `${used} / ${d.limit}${unit}` : used;
}

export function DegradedExplainSheet({ deviceId, deviceLabel, onClose }: { deviceId: string; deviceLabel: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<QuotaEntry | null>(null);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    void (async () => {
      // 即時重拉單 device 配額（§6.1 item 1）+ 次要拉訂閱取 retention_days（非阻斷）。
      const [qR, sR] = await Promise.allSettled([
        apiFetch<QuotaEntry[]>('/v3/me/quota', { query: { device_id: deviceId } }),
        apiFetch<SubscriptionLite>(`/v3/devices/${encodeURIComponent(deviceId)}/subscription`),
      ]);
      if (!alive.current) return;
      if (qR.status === 'fulfilled' && Array.isArray(qR.value.data)) {
        const e = qR.value.data.find((x) => x.device_id === deviceId) ?? qR.value.data[0] ?? null;
        setEntry(e);
        setState('ready');
      } else {
        setState('error');
      }
      if (sR.status === 'fulfilled' && sR.value.data && typeof sR.value.data.retention_days === 'number') {
        setRetentionDays(sR.value.data.retention_days);
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [deviceId]);

  // ESC 關閉（對齊 ConfirmDialog 互動慣例）。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dims = entry?.quota ? Object.entries(entry.quota) : [];
  const overDims = dims.filter(([, d]) => d.status === 'over');
  const warnDims = dims.filter(([, d]) => d.status === 'warning');
  const affected = overDims.length > 0 ? overDims : warnDims;
  const isOver = overDims.length > 0;

  const goUpsell = () => {
    // Quota.UpsellTapped（13 §6 / 19 §9）：analytics sink 尚未接（spec 13 deferred）→ 暫不發。
    onClose();
    navigate('/account/subscription');
  };

  return (
    <div
      className="dv-sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('account.quotaDetailsDialogLabel')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dv-sheet">
        <span className="dv-sheet-grip" aria-hidden="true" />
        <header style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
          <h2 style={{ color: isOver ? 'var(--color-semantic-danger)' : 'var(--color-semantic-warning)' }}>{isOver ? t('account.recordingDegraded') : t('account.usageNearLimit')}</h2>
          <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
            {deviceLabel}
          </p>
        </header>

        {state === 'loading' && (
          <p className="text-secondary" style={{ margin: 0 }} aria-busy="true">
            {t('account.loadingQuotaDetails')}
          </p>
        )}

        {state === 'error' && (
          <p className="text-secondary" style={{ margin: 0 }}>
            {t('account.loadQuotaDetailsFailed')}
          </p>
        )}

        {state === 'ready' && affected.length === 0 && (
          <p className="text-secondary" style={{ margin: 0 }}>
            {t('account.quotaNoAffected')}
          </p>
        )}

        {state === 'ready' &&
          affected.map(([key, d]) => {
            const label = QUOTA_DIMENSION_LABEL_KEY[key] ? t(QUOTA_DIMENSION_LABEL_KEY[key]) : key;
            const unit = QUOTA_DIMENSION_UNIT_KEY[key] ? t(QUOTA_DIMENSION_UNIT_KEY[key]) : '';
            const pct = quotaPercent(d.usage_ratio);
            const over = d.status === 'over';
            return (
              <div key={key} className="dv-sheet-dim">
                <div className="dv-sheet-dim-head">
                  <span>{label}</span>
                  <span style={{ color: over ? 'var(--color-semantic-danger)' : 'var(--color-semantic-warning)', fontFamily: 'var(--font-family-mono)' }}>
                    {dimNumbers(d, unit)}
                    {pct != null ? `（${pct}%）` : ''}
                  </span>
                </div>
                <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
                  {over ? explainOver(key, retentionDays, t) : explainWarning(key, t)}
                </p>
              </div>
            );
          })}

        <div className="dv-sheet-actions">
          <button className="btn-ghost" onClick={onClose}>
            {t('account.ctaLater')}
          </button>
          <button className="btn-primary" onClick={goUpsell}>
            {t('account.ctaUpgradeRenew')}
          </button>
        </div>
      </div>
    </div>
  );
}
